import React, { useState, useEffect, useRef } from "react";
// REDUX
import { useSelector, useDispatch, useStore } from "react-redux";
import { updateFilters, updateBlockFilters } from "../../../redux/reports/reportsSlice";
import { usePostCarUseComparisonsMutation } from "../../../redux/reports/chartsAPI";
import useAllBanksMutation from "../useAllBanksMutation";
// COMPONENTS
import ReportsHeader from "../ReportsHeader";
import ChartBar from "../charts/ChartBar";
import ChartPie from "../charts/ChartPie";
import ChartLine from "../charts/ChartLine";
// AUTHORIZATION
import { useScreenPermission } from "../../../customHooks/useScopedPermission";
import UnauthorizedPage from "../../unauthorized/UnauthorizedPage";
// INTERFACES
import { RootState } from "../../../app/store";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { getTotalCarsInBank, GenerateCarUseComparisons } from "../utils";

const ReportCarUseChartComparisons = () => {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const store = useStore();

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [chartData, setChartData] = useState<any>([]);
  const skipFirstFiltersFetch = useRef(true);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);
  const { filters, reportRefreshTable } = useSelector((state: RootState) => state.report);
  // Reads SCREEN_PERMISSION["car-use-chart-comparisons"] -- the same
  // declaration Layout.tsx's sidebar link reads.
  const allowed = useScreenPermission("car-use-chart-comparisons");

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetChart, { data, isLoading, isError, error }] = useAllBanksMutation(
    usePostCarUseComparisonsMutation(),
    "perLift",
  );

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getChartData = async () => {
    const token = await GETJwtToken();
    triggerGetChart({
      token,
      body: {
        bank_id: filters.bankId,
        start_date: filters.startDate,
        end_date: filters.endDate,
        timezone: filters.timezoneCode,
        lift_type: "ELEVATOR",
      },
    });
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    if (data && data.results) {
      const response = GenerateCarUseComparisons(data.results);
      setChartData(response);
    } else {
      setChartData([{ value: 0, tag: "", name: "" }]);
    }
  }, [data]);

  useEffect(() => {
    if (skipFirstFiltersFetch.current) {
      skipFirstFiltersFetch.current = false;
      return;
    }

    if (
      filters.bankId !== "" &&
      filters.startDate !== "" &&
      filters.endDate !== "" &&
      filters.timezoneCode !== "" &&
      filters.liftType === "ELEVATOR" &&
      !isLoading
    ) {
      getChartData();
    }
  }, [filters.bankId, filters.startDate, filters.endDate, filters.timezoneCode, filters.liftType]);

  useEffect(() => {
    if (chartData && chartData.length > 0) {
      const response = getTotalCarsInBank(chartData);
      // Get the current state from Redux store to avoid stale closure values
      const currentFilters = (store.getState() as RootState).report.filters;
      dispatch(
        updateFilters({
          ...currentFilters,
          selectedCarsList: response,
          carsList: response,
        }),
      );
    }
  }, [chartData, dispatch, store]);

  useEffect(() => {
    dispatch(updateBlockFilters(isLoading));
    return () => {
      dispatch(updateBlockFilters(false));
    };
  }, [isLoading]);

  useEffect(() => {
    if (reportRefreshTable) {
      getChartData();
    }
  }, [reportRefreshTable]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      {allowed ? (
        <div className={`layout__contentPage__${theme}`}>
          <ReportsHeader
            reportDefaultData={{
              reportType: "CHART",
              levelTypeName: "BANKS",
              allowAllBanks: true,
              displaySelectTypeLevel: false,
              liftType: "ELEVATOR",
              defaultColumns: [],
              defaultOffsets: [],
              infoDescriptionText: "report.infoDescriptionText2",
              displayDownloadCSVReport: false,
              displayDownloadPDFReport: true,
              reportTitle: "Car Use / Car Comparisons",
              displayAdvanceFiltersExclusions: false,
              displayAdvanceFiltersOffsets: false,
              disabledOffsets: true,
              useTablePagination: true,
              displayDateByRange: false,
              displayDate: true,
              displaySelectLiftType: false,
              displayAdvanceFilterCharts: true,
              displayAdvanceFiltersTable: false,
              displayRecentWeeksPicket: false,
              displayInputLimitItems: false,
              displayChartMirror: false,
              chartMirror: false,
              chartType: "bar_vertical",
              dateTypeOptions: [{ id: "TODAY", name: "Day" }],
              dateDaysLimit: 0,
              displayMinimumActivation: false,
              dateWeeksLimit: 0,
              dateMonthsLimit: 0,
              dateQuarterLimit: 0,
              dateYearsLimit: 0,
              dateType: "TODAY",
              bankSearchAndFilterString: "&filter_target=type&filter=ELEVATOR",
            }}
          />
          {filters.chartType === "pie" && <ChartPie charData={[]} />}
          {(filters.chartType === "line" ||
            filters.chartType === "line_natural" ||
            filters.chartType === "step_before" ||
            filters.chartType === "step_after") && (
            <ChartLine
              data={chartData}
              isLoading={isLoading}
              isError={isError}
              errorMessage={error && (error as any).data?.message}
              bottomLabel="CAR"
              leftLabel="COUNT"
            />
          )}
          {(filters.chartType === "bar_vertical" ||
            filters.chartType === "bar_stack_vertical" ||
            filters.chartType === "bar_horizontal" ||
            filters.chartType === "bar_stack_horizontal") && (
            <>
              <ChartBar
                data={chartData}
                isLoading={isLoading}
                isError={isError}
                errorMessage={error && (error as any).data?.message}
                bottomLabel="CAR"
                leftLabel="COUNT"
              />
            </>
          )}
        </div>
      ) : (
        <UnauthorizedPage />
      )}
    </div>
  );
};

export default ReportCarUseChartComparisons;
